<template>
  <transition name="brick-fade-slide">
    <div class="nzbrick" :class="{ 'fixed-brick': isFixed }" :data-brick-id="brick.id" :data-brick-img="brick.cpimage">
      <div class="nzbrick-inner">

        <!-- TOOLS BAR -->
        <div v-if="!isFixed" class="nzbrick-tools">
          <span
            v-if="!isFixed"
            class="icon-move drag-handle"
            @mousedown.stop
            @touchstart.stop
          ></span>
          <span
            v-if="!isFixed"
            class="icon-cog"
            @click="openTypeSwitcherForUpdate"
          ></span>
          <span
            v-if="!isFixed"
            class="icon-trash"
            @click="removeRecord"
          ></span>
        </div>

        <!-- BRICK CONTENT -->
        <div
            class="nzbrick-edit"
            :class="{
              ['nzedit-' + brick.cptype]: true,
              'nzedit-fixed': isFixed
            }"
          >
          <!-- TITLE -->
          <template v-if="brick.cptype === 'cptitle'">
            <h3
              ref="titleEl"
              class="brick-title editable"
              contenteditable="true"
              spellcheck="false"
              @focus="onTitleFocus"
              @input="onTitleInput"
              @blur="onTitleBlur"
              @keydown="onTitleKeydown"
            ></h3>
            <div class="title-sync" v-show="!isTitleEditing">{{ localTitle }}</div>
            <small v-if="saveError">{{ saveError }}</small>
          </template>

          <!-- TEXT -->
          <template v-else-if="brick.cptype === 'cptext'">
            <div
              v-if="!isTextEditing"
              class="brick-text"
              v-html="brick.cptext"
              @click="activateTextEditor"
            ></div>

            <transition name="fade">
              <div v-if="isTextEditing" class="text-editor-wrapper">
                <div ref="pellContainer" class="pell-container"></div>
              </div>
            </transition>

            <div class="cptext-btns">
              <button
                v-if="isTextEditing"
                class="btn btn-warning"
                @click="cancelText"
              >
                Cancel
              </button>
              <button
                v-if="isTextEditing"
                class="btn btn-primary"
                @click="saveText"
              >
                Save
              </button>
            </div>
          </template>

          <!-- IMAGE -->
          <template v-else-if="brick.cptype === 'cpimage'">

            <div
              :class="{
                'brick-image-wrap': true,
                'default-img': brick.cpimage === ''
              }"
              @click="openUploader"
              >
              <img 
                class="brick-image"
                :src="resolveImage(brick.cpimage)"
                :alt="brick.cpimage"
                @click="openUploader"
              />
            </div>

          </template>

          <!-- GALLERY -->
          <template v-else-if="brick.cptype === 'cpimggallery'">

            <div v-if="isGalleryLoading">Loading…</div>

            <div
              v-else-if="galleryImages.length === 0"
              @click="openUploader"
            >
              <p>No images yet — click to upload</p>
            </div>

            <swiper
              v-else
              :key="swiperNonce"
              :modules="swiperModules"
              navigation
              :pagination="{ clickable: true }"
              effect="fade"
              :fade-effect="{ crossFade: true }"
              :loop="true"
              :autoplay="{ delay: 3500, disableOnInteraction: false }"
              class="cp-gallery-swiper"
            >
              <swiper-slide
                v-for="img in galleryImages"
                :key="img.name"
                class="gallery-slide-wrapper"
              >
                <button
                  class="gallery-delete-btn"
                  @click.stop="deleteGalleryImage(img)"
                >
                  <span class="icon-trash"></span>
                </button>

                <img
                  class="gallery-img"
                  :src="resolveGalleryImg(img.name)"
                  :alt="img.name"
                />
              </swiper-slide>
            </swiper>

            <button
              v-if="galleryImages.length"
              class="btn btn-icon"
              @click="openUploader"
            >
              <span class="icon-pencil"></span>
            </button>
          </template>

          <!-- FILE LINK (cpappfilelink) -->
          <template v-else-if="brick.cptype === 'cpappfilelink'">

            <div @click="openUploader" class="cpappfilelink-wrapper">
              <div class="cpappfilelink-file">
                <span class="icon-file-preview-pdf"></span>
                <h3>{{ brick.cptitle }}</h3>
                <span class="cpappfilelink-file-name"> - ({{ brick.cpappfilelink || "No file yet" }})</span>
              </div>
            </div>

          </template>

          <!-- UNKNOWN -->
          <template v-else>
            <div>Unknown type: {{ brick.cptype }}</div>
          </template>
        </div>

        <!-- ADD BUTTON -->
        <button
          v-if="shouldShowAddButton"
          class="btn btn-icon add-brick-btn"
          @click="openTypeSwitcherForAdd"
        >
          <span class="icon-plus"></span>
        </button>

      </div>
    </div>
  </transition>

  <!-- TYPE SWITCHER -->
  <teleport to=".nz-modal-body">
    <transition name="modal-fade-scale">
      <div v-if="showTypeSwitcher" class="modal-global-overlay">
        <div class="type-switcher-box">
          <h2>{{ typeSwitcherTitle }}</h2>

          <div class="type-switcher-grid">
            <div
              v-for="t in availableTypes"
              :key="t"
              class="type-switcher-item"
              @click="selectType(t)"
            >
              <div
                v-if="TYPE_PREVIEWS[t]"
                v-html="TYPE_PREVIEWS[t].html"
              ></div>
              <small>{{ t }}</small>
            </div>
          </div>

          <button
            class="btn btn-secondary"
            @click="showTypeSwitcher = false"
          >
            Cancel
          </button>
        </div>
      </div>
    </transition>
  </teleport>

  <!-- UNIVERSAL UPLOADER -->
  <nz-uploader
    :visible="showUploader"

    :selected="brick.cptype === 'cpappfilelink'
      ? brick.cpappfilelink
      : brick.cpimage"
    
    :file-title="brick.cptype === 'cpappfilelink'
      ? brick.cptitle
      : ''"

    :customer-id="customerId"
    :article-id="articleId"

    :blockname="
      brick.cptype === 'cpimggallery'
        ? `gallery_img_${brick.id}`
        : blockname
    "

    :mode="
        brick.cptype === 'cpimage'
          ? 'single'
        : brick.cptype === 'cpimggallery'
          ? 'gallery'
          : 'file'
    "

    :brick-id="brick.id"
    :brick-img="brick.cpimage"
    :lang="brick.lang"

    @selected="onUploaderSelected"
    @single-deleted="onSingleDeleted"
    @gallery-updated="onGalleryUpdated"
    @title-changed="savePdfTitle"
    @close="showUploader = false"
  />

</template>

<script>
import pell from "pell";
import NzUploader from "@/components/uploader/NzUploader.vue";
import { loadCustomerConfig } from "@/utils/loadCustomerConfig.js";

import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import ModalConfirm from "@/components/modal/ModalConfirm.vue";
import ModalManager from "@/components/modal/ModalManager";

import "swiper/css";
/*import "swiper/css/navigation";*/
/*import "swiper/css/pagination";*/
import "swiper/css/effect-fade";

const API = import.meta.env.VITE_API_URL || '';

export default {
  name: "NzBrick",
  components: { NzUploader, Swiper, SwiperSlide },
  emits: {
    updated: brick => typeof brick === "object",
    removed: id => typeof id === "number" || typeof id === "string",
    addbrick: payload => payload && typeof payload === "object",
    edit: () => true
  },
  props: {
    brick: Object,
    index: Number,
    fixedCount: Number,
    totalBricks: Number,
    selectedTitle: String
  },

  data() {
    return {
      localTitle: this.brick.cptitle || "",
      localText: this.brick.cptext || "",
      isTitleEditing: false,
      isTextEditing: false,
      showTypeSwitcher: false,
      showUploader: false,
      typeSwitcherMode: "update",

      saveError: null,
      pellEditor: null,

      customerId: parseInt(localStorage.getItem("customer_id"), 10) || 1,
      availableTypes: [],
      fixedcptypes: [],
      defaults: {},
      defaultImg: "",
      blockname: "",

      TYPE_PREVIEWS: {
        cptitle: { html: `<div>Title</div>` },
        cptext: { html: `<div>Text</div>` },
        cpimage: { html: `<div>Image</div>` },
        cpimggallery: { html: `<div>Gallery</div>` },
        cpappfilelink: { html: `<div>PDF</div>` }
      },

      swiperModules: [Navigation, Pagination, Autoplay, EffectFade],
      galleryImages: [],
      isGalleryLoading: false,
      swiperNonce: 1,
    };
  },

  computed: {
    isFixed() {
      return this.index < this.fixedCount;
    },
    shouldShowAddButton() {
      return (
        this.index === this.totalBricks - 1 ||
        this.index >= this.fixedCount - 1
      );
    },
    typeSwitcherTitle() {
      return this.typeSwitcherMode === "add"
        ? "Add Brick"
        : "Switch Brick Type";
    },
    lang() {
      return localStorage.getItem("lang") || "en";
    },
    articleId() {
      return this.brick.article_id;
    },
  },

  async mounted() {
    await this.loadCustomerConfig();
    this.setTitleElement();

    if (this.brick.cptype === "cpimggallery") {
      this.loadGallery();
    }
  },

  updated() {
    this.setTitleElement();
  },

  methods: {
    async loadCustomerConfig() {
      const cfg = await loadCustomerConfig();
      this.availableTypes = cfg?.articles?.cptypes || [];
      this.fixedcptypes = cfg?.articles?.fixedcptypes || [];
      this.defaults = cfg?.articles?.defaults || {};
      this.defaultImg = this.defaults.content.cpimage || "";
      this.blockname = cfg?.articles?.blockname || "maincontent";

    },

    setTitleElement() {
      if (this.brick.cptype !== "cptitle") return;
      if (this.isTitleEditing) return;
      if (!this.$refs.titleEl) return;

      this.$refs.titleEl.innerText = this.localTitle || "";
    },

    /** When a PDF title changes inside uploader */
    async savePdfTitle(newTitle) {
      if (newTitle === this.brick.cptitle) return;

      const apiKey = localStorage.getItem("api_key");

      await fetch(`/api/bricks/${this.brick.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({ cptitle: newTitle }),
      });

      this.brick.cptitle = newTitle; // <- updates brick UI immediately
      this.$emit("updated", this.brick);
    },

    async savePdfFile(name) {
      const apiKey = localStorage.getItem("api_key");

      await fetch(`/api/bricks/${this.brick.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({ cpappfilelink: name }),
      });

      this.brick.cpappfilelink = name;
      this.$emit("updated", this.brick);
    },    

    /* IMAGES */
    resolveImage(img) {
      if (!img) return `/config/customer_${this.customerId}/assets/img/${this.defaultImg}`;
      const v = Date.now()
      return `${API}/uploads/customer_${this.customerId}/articles/id_${this.articleId}/${this.blockname}/medium/${img}?v=${v}`;
    },

    resolveGalleryImg(name) {
      const v = Date.now();
      return `${API}/uploads/customer_${this.customerId}/articles/id_${this.articleId}/gallery_img_${this.brick.id}/medium/${name}?v=${v}`;
    },

    async loadGallery() {
      this.isGalleryLoading = true;

      const url = `${API}/api/gallery/${this.customerId}/${this.articleId}/gallery_img_${this.brick.id}/${this.brick.lang}`;
      const res = await fetch(url);
      const json = await res.json();

      this.galleryImages = json.entries || [];
      this.swiperNonce++;

      this.isGalleryLoading = false;
    },

    async deleteGalleryImage(img) {
      const apiKey = localStorage.getItem("api_key");

      await fetch(`/api/upload/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({
          customerId: this.customerId,
          articleId: this.articleId,
          blockname: `gallery_img_${this.brick.id}`,
          lang: this.lang,
          name: img.name,
        }),
      });

      await this.loadGallery();
    },

    async onGalleryUpdated() {
      await this.loadGallery();
    },

    async onSingleDeleted(img) {
      if( this.brick.cpimage === img ){
        this.onUploaderSelected("");

      }

    },

    /* UPLOADER RESULT */
    async onUploaderSelected(name) {
      this.showUploader = false;

      if (this.brick.cptype === "cpimggallery") {
        await this.loadGallery();
        return;
      }

      const apiKey = localStorage.getItem("api_key");
      let payload;

      if (this.brick.cptype === "cpappfilelink") {
        payload = { cpappfilelink: name };
      } else {
        payload = { cpimage: name };
      }

      const res = await fetch(`/api/bricks/${this.brick.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        alert("Failed to save brick");
        return;
      }

      if (this.brick.cptype === "cpappfilelink") {
        this.brick.cpappfilelink = name;
      } else {
        this.brick.cpimage = name;
      }
    },

    /* TYPE SWITCHER */
    openTypeSwitcherForUpdate() {
      if (!this.isFixed) {
        this.typeSwitcherMode = "update";
        this.showTypeSwitcher = true;
      }
    },

    openTypeSwitcherForAdd() {
      const nextFixed = this.fixedcptypes[this.index + 1];
      if (nextFixed) {
        this.$emit("addbrick", { afterId: this.brick.id, type: nextFixed });
        return;
      }
      this.typeSwitcherMode = "add";
      this.showTypeSwitcher = true;
    },

    async selectType(t) {
      if (this.typeSwitcherMode === "add") {
        this.$emit("addbrick", { afterId: this.brick.id, type: t });
      } else {
        const apiKey = localStorage.getItem("api_key");
        await fetch(`/api/bricks/${this.brick.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(apiKey ? { "x-api-key": apiKey } : {}),
          },
          body: JSON.stringify({ cptype: t }),
        });
        this.brick.cptype = t;

        if (t === "cpimggallery") {
          this.loadGallery();
        }
      }
      this.showTypeSwitcher = false;
    },

    /* DELETE BRICK */
    async removeRecord() {

      
      const confirmed = await ModalManager.confirm({
        title: "Remove this content element?",
        props: {
          message: "This action cannot be undone.",
          confirmLabel: "Remove"
        }
      });

      if (!confirmed) return;

      const apiKey = localStorage.getItem("api_key");

      await fetch(`/api/bricks/${this.brick.id}`, {
        method: "DELETE",
        headers: {
          ...(apiKey ? { "x-api-key": apiKey } : {})
        }
      });

      this.$emit("removed", this.brick.id);
    },
    /* TEXT EDITOR */
    activateTextEditor() {
      this.isTextEditing = true;
      this.$nextTick(() => {
        this.pellEditor = pell.init({
          element: this.$refs.pellContainer,
            actions: [
              'bold',
              'italic',
              'underline',
              'heading2',
              {
                name: 'heading3',
                icon: '<b>H<sub>3</sub></b>',
                title: 'Heading 3',
                result: () => document.execCommand('formatBlock', false, 'h3')
              },
              {
                name: 'heading4',
                icon: '<b>H<sub>4</sub></b>',
                title: 'Heading 4',
                result: () => document.execCommand('formatBlock', false, 'h4')
              },
              'quote',
              'olist',
              'ulist',
              'link'
            ],
          onChange: html => (this.localText = html),
        });
        this.pellEditor.content.innerHTML = this.localText;
      });
    },

    async saveText() {
      const html = this.localText;
      const apiKey = localStorage.getItem("api_key");

      await fetch(`/api/bricks/${this.brick.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({ cptext: html }),
      });

      this.brick.cptext = html;
      this.isTextEditing = false;
    },

    cancelText() {
      this.localText = this.brick.cptext || "";
      this.isTextEditing = false;
    },

    /* TITLE EDITING */
    onTitleFocus() {
      this.isTitleEditing = true;
      if (this.$refs.titleEl) {
        this.$refs.titleEl.innerText = this.localTitle;
      }
    },
    onTitleInput(e) {
      this.localTitle = e.target.innerText;
    },
    onTitleKeydown(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        e.target.blur();
      }
    },
    async onTitleBlur(e) {
      this.isTitleEditing = false;
      const title = this.localTitle;
      const apiKey = localStorage.getItem("api_key");

      const res = await fetch(`/api/bricks/${this.brick.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({ cptitle: title }),
      });

      if (!res.ok) {
        this.saveError = "Failed to save title";
      } else {
        this.brick.cptitle = title;
      }

      if (this.$refs.titleEl) {
        this.$refs.titleEl.innerText = this.localTitle;
      }
    },

    /* OPEN UPLOADER */
    openUploader() {
      this.showUploader = true;
    },
  },
};
</script>

<style lang="less">

/*────────────────────────────────────────────
  BRICK ANIMATION
────────────────────────────────────────────*/
.brick-fade-slide-enter-active,
.brick-fade-slide-leave-active {
  transition: all 0.25s ease;
}
.brick-fade-slide-enter-from,
.brick-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/*────────────────────────────────────────────
  MODAL ANIMATIONS
────────────────────────────────────────────*/
.modal-fade-scale-enter-active,
.modal-fade-scale-leave-active {
  transition: all .25s ease;
}
.modal-fade-scale-enter-from,
.modal-fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/*────────────────────────────────────────────
  BRICK WRAPPER
────────────────────────────────────────────*/
.nzbrick {
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
  padding: 0.5rem 0.75rem;
  margin-bottom: 12px;
}
.nzbrick-inner {
  position: relative;
  width: 100%;
}

/* FIXED BRICKS LOOK DISABLED */
.fixed-brick {
  opacity: 0.9;
}

/*────────────────────────────────────────────
  TOOLBAR
────────────────────────────────────────────*/
.nzbrick-tools {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  gap: 10px;
}
.nzbrick-tools span {
  font-size: 18px;
  cursor: pointer;
  opacity: .65;

  &:hover {
    opacity: 1;
  }
}

/*────────────────────────────────────────────
  CONTENT
────────────────────────────────────────────*/
.brick-title {
  margin: 0;
  font-size: 1.2rem;
}
.title-sync {
  visibility: hidden;
  height: 0;
  overflow: hidden;
}
.brick-text {
  cursor: text;
  line-height: 1.45;
}

.pell-container {
  border: 1px solid #ddd;
  background: #fff;
  min-height: 180px;
}

.error-msg {
  color: @danger;
  font-size: 0.85rem;
}

.cptext-btns{
  padding: 12px 0px 0px 12px;
  button:first-child{
    margin-right: 40px;
  }
}

/*────────────────────────────────────────────
  ADD BUTTON
────────────────────────────────────────────*/
.add-brick-btn {
  position: absolute;
  bottom: -12px;
  right: -12px;
  padding: 8px;
  border-radius: 50%;
  background: @primary;
  color: white;
  box-shadow: 0 3px 10px rgba(0,0,0,0.2);
  cursor: pointer;

  &:hover {
    background: darken(@primary, 8%);
  }
}

/*────────────────────────────────────────────
  GLOBAL MODAL
────────────────────────────────────────────*/
.modal-global-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* DELETE MODAL */
.modal-box {
  background: #fff;
  padding: 1.5rem 2rem;
  border-radius: 10px;
  width: 90%;
  max-width: 380px;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
}
.modal-actions {
  margin-top: 1.4rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* TYPE SWITCHER */
.type-switcher-box {
  background: #fff;
  padding: 1.5rem 2rem;
  border-radius: 10px;
  width: 95%;
  max-width: 700px;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
  h2{
    margin-top: 0px;
  }
  button.btn-secondary{
    margin-top: 20px;

  }
}

.type-switcher-grid {
  margin-top: 1rem;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
}

.type-switcher-item {
  cursor: pointer;
  background: #f7f7f7;
  padding: 1rem;
  border-radius: 8px;
  transition: .15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .5rem;

  &:hover {
    background: #eee;
    transform: translateY(-3px);
  }
}

/* PREVIEWS */
.preview-wrapper {
  width: 100%;
  height: 60px;
  background: #fafafa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-title {
  font-size: 16px;
  font-weight: bold;
  color: #444;
}
.preview-text {
  font-size: 12px;
  color: #666;
  padding: 4px;
  line-height: 1.4;
}
.preview-image {
  width: 100%;
  height: 100%;
  background: #ddd;
  border-radius: 4px;
}


/* SWIPER */
.gallery-edit{
  width: 100%;
  padding: 12px 0px;
  text-align: center;
  button{
    display: block;
    margin: 0 auto;
  }
}
.cp-gallery-swiper {
  width: 100%;
  height: 320px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.cp-gallery-swiper img.gallery-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-empty {
  padding: 40px;
  border: 2px dashed #ccc;
  text-align: center;
  cursor: pointer;
  border-radius: 8px;
}

.gallery-loading {
  text-align: center;
  padding: 20px;
}

.gallery-slide-wrapper {
  position: relative;

}

.gallery-delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;
  background: rgba(0,0,0,0.55);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
}

.gallery-delete-btn:hover {
  background: rgba(255,0,0,0.8);
}
.cp-gallery-swiper .swiper-slide {
  opacity: 0 !important;
  transition: opacity 0.6s ease-in-out;
}

.cp-gallery-swiper .swiper-slide.swiper-slide-active {
  opacity: 1 !important;
}

/* END SWIPER */

</style>
